import { peekActiveCompanyKey } from "utils/encryptForCompany";
import pluginService from "api/pluginService";
import { useAuth } from "react-oidc-context";
import { getKeys } from "utils/getKeys";
import { getUserId } from "utils/getUserId";
import {
  isPluginConnectionStale,
  resolvePluginConnectionKeys,
  setConnectedPluginCompanyKeyIdentity,
} from "utils/pluginConnection";

export function usePlugin() {
  const { mutate: createConnection } = pluginService.useCreateConnection();
  const { mutate: setConnection } = pluginService.useSetConnection();
  // bez AuthProvidera (aplikacja EBI, ebi/) useAuth() zwraca undefined
  const auth = useAuth();
  const userId = getUserId(auth?.user);

  const connect = (): Promise<void> => {
    return new Promise<void>(async (resolve, reject) => {
      if (!auth?.isAuthenticated) return;
      const keys = await getKeys(userId);
      if (!keys) return reject();

      // Klucz FIRMY (a nie intermediate) — od F3 wtyczka nim odszyfrowuje UserKey
      // obiektów; szczegóły i to, co zostało, w utils/pluginConnection.ts.
      let PrivateKey: string;
      let PublicKey: string;
      let CompanyKeyIdentity: string | null;
      try {
        ({ PrivateKey, PublicKey, CompanyKeyIdentity } =
          await resolvePluginConnectionKeys(keys));
      } catch {
        // Bez materiału klucza nie ma czego wysłać wtyczce — połączenie się nie uda.
        return reject();
      }

      createConnection(
        {
          AppId: "todo",
          Version: "1.0.0.0",
        },
        {
          onSuccess: (response) => {
            setConnection(
              {
                PrivateKey,
                PublicKey,
                //Token: response.data.Token,
                Token: response.data.AccessToken,
                RefreshToken: response.data.RefreshToken,
                Url: response.data.Url,
              },
              {
                onSuccess: () => {
                  setConnectedPluginCompanyKeyIdentity(CompanyKeyIdentity);
                  resolve();
                },
                onError: () => {
                  reject();
                },
              },
            );
          },
          onError: () => {
            reject();
          },
        },
      );
    });
  };

  // Wtyczka dostaje token tylko z POST /api/connect (PluginContext łączy raz, przy starcie
  // widoku). Po restarcie wtyczki w trakcie sesji jej token jest pusty i każde wywołanie
  // wymagające API Noty kończy się 400 „IDX10000 ... 'token' cannot be null" — przed takim
  // wywołaniem trzeba sprawdzić stan połączenia i w razie potrzeby połączyć ponownie.
  const ensureConnected = async (): Promise<void> => {
    const version = await pluginService.getVersion();
    if (!version.IsConnected || isPluginConnectionStale(peekActiveCompanyKey())) {
      await connect();
    }
  };

  return {
    connect,
    ensureConnected,
    // Wtyczka trzyma klucz z ostatniego połączenia — po rotacji generacji klucza firmy
    // albo zmianie firmy trzeba połączyć się ponownie (samo IsConnected tego nie wykryje).
    isConnectionStale: isPluginConnectionStale(peekActiveCompanyKey()),
  };
}

export default usePlugin;
